'use client';

import * as TooltipPrimitive from '@radix-ui/react-tooltip';
import * as React from 'react';
import { cn } from '@/lib/utils';

const TooltipProvider = TooltipPrimitive.Provider;

const Tooltip = TooltipPrimitive.Root;

const TooltipTrigger = React.forwardRef<
    React.ElementRef<typeof TooltipPrimitive.Trigger>,
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Trigger>
>(({ onFocus, ...props }, ref) => (
    <TooltipPrimitive.Trigger
        ref={ref}
        {...props}
        onFocus={(event) => {
            onFocus?.(event);
            if (event.defaultPrevented) return;

            // Radix opens the tooltip on any focus, not just keyboard focus, and
            // that path skips `delayDuration` entirely. A dialog that sends focus
            // to the first control in its body would pop that control's tooltip
            // open with no one hovering it. Preventing the event here stops Radix's
            // own handler (it composes ours first and bails on `defaultPrevented`),
            // so keyboard focus still shows the tooltip and programmatic focus does
            // not. `target`, not `currentTarget`: React listens to `focusin`, which
            // bubbles, and a trigger can wrap the element that actually took focus.
            const target = event.target as HTMLElement | null;
            if (!target?.matches?.(':focus-visible')) event.preventDefault();
        }}
    />
));
TooltipTrigger.displayName = TooltipPrimitive.Trigger.displayName;

const TooltipContent = React.forwardRef<
    React.ElementRef<typeof TooltipPrimitive.Content>,
    React.ComponentPropsWithoutRef<typeof TooltipPrimitive.Content>
>(({ className, sideOffset = 4, ...props }, ref) => (
    <TooltipPrimitive.Portal>
        <TooltipPrimitive.Content
            ref={ref}
            sideOffset={sideOffset}
            className={cn(
                'z-50 overflow-hidden rounded-md bg-primary px-3 py-1.5 text-xs text-primary-foreground animate-in fade-in-0 zoom-in-95 data-[state=closed]:animate-out data-[state=closed]:fade-out-0 data-[state=closed]:zoom-out-95 data-[side=bottom]:slide-in-from-top-2 data-[side=left]:slide-in-from-right-2 data-[side=right]:slide-in-from-left-2 data-[side=top]:slide-in-from-bottom-2',
                className,
            )}
            {...props}
        />
    </TooltipPrimitive.Portal>
));
TooltipContent.displayName = TooltipPrimitive.Content.displayName;

export { Tooltip, TooltipTrigger, TooltipContent, TooltipProvider };
